import {
  IChartApi,
  ISeriesApi,
  SeriesType,
  LineSeries,
  HistogramSeries,
  CandlestickSeries,
} from "lightweight-charts";

// --- TYPES ---
export type TF = "1min" | "2min" | "3min" | "5min" | "10min" | "15min" | "30min" | "60min" | "1d";

export interface Candle {
  time: number; // epoch seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface OhlcPayload {
  bars?: any[];
  candles?: any[];
  data?: any[];
  meta?: {
    source?: string;
    retryAfterSec?: number;
    retryAfterMs?: number;
    [k: string]: any;
  };
  error?: boolean;
  message?: string;
  status?: number;
  requestId?: string;
  [k: string]: any;
}

export interface IndicatorConfig {
  id: string;
  type: "SMA" | "EMA" | "VWAP" | "BB";
  period?: number;
  color: string;
  label: string;
  visible: boolean;
}

// --- HELPERS ---

export function toEpochSec(x: unknown): number | null {
  if (x == null) return null;
  if (x instanceof Date) return Math.floor(x.getTime() / 1000);
  if (typeof x === "number") return x > 10_000_000_000 ? Math.floor(x / 1000) : Math.floor(x);
  if (typeof x === "string") {
    const n = Number(x);
    if (Number.isFinite(n)) return toEpochSec(n);
    const d = new Date(x);
    return Number.isFinite(d.getTime()) ? Math.floor(d.getTime() / 1000) : null;
  }
  return null;
}

export function normalizeTf(tfRaw: unknown): TF {
  const tf = String(tfRaw || "1min").toLowerCase().trim();
  if (["1m", "1min", "1 minute"].includes(tf)) return "1min";
  if (["2m", "2min"].includes(tf)) return "2min";
  if (["3m", "3min"].includes(tf)) return "3min";
  if (["5m", "5min"].includes(tf)) return "5min";
  if (["10m", "10min"].includes(tf)) return "10min";
  if (["15m", "15min"].includes(tf)) return "15min";
  if (["30m", "30min"].includes(tf)) return "30min";
  if (["60m", "60min", "1h", "1hr"].includes(tf)) return "60min";
  if (["1d", "day"].includes(tf)) return "1d";
  return "1min";
}

export function formatDateNY(epochSec: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(epochSec * 1000));
}

export function pickSymbol(props: any): string {
  const v =
    props?.symbol ??
    props?.ticker ??
    props?.trade?.symbol ??
    props?.trade?.ticker ??
    props?.selectedTrade?.symbol ??
    props?.selectedTrade?.ticker;
  return v ? String(v).toUpperCase().trim() : "";
}

export function pickSecType(props: any): string {
  const v =
    props?.secType ??
    props?.trade?.secType ??
    props?.trade?.sec_type ??
    props?.trade?.instrumentType ??
    props?.trade?.assetClass ??
    props?.selectedTrade?.secType ??
    props?.selectedTrade?.instrumentType ??
    props?.selectedTrade?.assetClass;
  const s = v ? String(v).toUpperCase().trim() : "STK";
  if (s === "STOCK" || s === "EQUITY") return "STK";
  if (s === "STOCKS" || s === "EQ") return "STK";
  if (s === "FOREX" || s === "FX") return "CASH";
  if (s === "CURRENCY" || s === "CASH") return "CASH";
  if (s === "FUTURE" || s === "FUTURES") return "FUT";
  if (s === "CRYPTO" || s === "CRYPTOCURRENCY") return "CRYPTO";
  return s;
}

export function pickBestTimestampSec(props: any): number {
  const candidates: unknown[] = [
    props?.date,
    props?.trade?.date,
    props?.trade?.entryTime,
    props?.trade?.openedAt,
    props?.trade?.openTime,
    props?.trade?.createdAt,
    props?.selectedTrade?.entryTime,
    props?.selectedTrade?.openedAt,
    props?.executions?.[0]?.timeSec,
    props?.executions?.[0]?.timestamp,
    props?.fills?.[0]?.timeSec,
    props?.fills?.[0]?.timestamp,
  ];
  for (const c of candidates) {
    const t = toEpochSec(c);
    if (t != null && t > 0) return t;
  }
  return Math.floor(Date.now() / 1000);
}

export function normalizeCandles(payload: OhlcPayload): Candle[] {
  // Defensive extraction: the server may return several shapes:
  // - { candles: [...] }
  // - { bars: [...] }
  // - { bars: { bars: [...] , meta: {...} } }
  // - { data: [...] }
  // - sometimes the top-level payload itself may be an array
  const extractArray = (p: any): any[] => {
    if (!p) return [];
    if (Array.isArray(p)) return p;
    if (Array.isArray(p.candles)) return p.candles;
    if (Array.isArray(p.bars)) return p.bars;
    if (Array.isArray(p.data)) return p.data;
    if (p.bars && Array.isArray(p.bars.bars)) return p.bars.bars;
    if (p.bars && Array.isArray(p.bars.data)) return p.bars.data;
    if (p.data && Array.isArray(p.data.data)) return p.data.data;
    // fallback: attempt to find the first array anywhere inside the object (shallow)
    for (const k of Object.keys(p)) {
      if (Array.isArray(p[k])) return p[k];
    }
    return [];
  };

  const arr: any[] = extractArray(payload) || [];

  return arr
    .map((b: any) => {
      const t = toEpochSec(b?.time ?? b?.t ?? b?.timeSec ?? b?.timestamp);
      if (!t) return null;
      const o = Number(b?.open ?? b?.o);
      const h = Number(b?.high ?? b?.h);
      const l = Number(b?.low ?? b?.l);
      const c = Number(b?.close ?? b?.c);
      const v = Number(b?.volume ?? b?.vol ?? b?.v ?? 0);
      if (![o, h, l, c].every(Number.isFinite)) return null;
      return { time: t, open: o, high: h, low: l, close: c, volume: v } as Candle;
    })
    .filter((x: Candle | null): x is Candle => x !== null)
    .sort((a: Candle, b: Candle) => a.time - b.time);
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// --- FETCHING ---

export function buildBaseCandidates(): string[] {
  let envBase = "";
  try {
    envBase = import.meta.env?.VITE_API_BASE || "";
  } catch {}
  const cleanEnv = envBase.replace(/\/+$/, "");
  const bases: string[] = [];
  if (cleanEnv) bases.push(cleanEnv);
  bases.push(""); 
  return Array.from(new Set(bases));
}

export async function fetchJsonWithTimeout(url: string, signal: AbortSignal, timeoutMs: number, init?: RequestInit): Promise<OhlcPayload> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  signal.addEventListener("abort", onAbort, { once: true });
  try {
    const res = await fetch(url, { ...(init || {}), signal: ctrl.signal });
    let payload: OhlcPayload = {};
    try {
      payload = await res.json();
    } catch {
      // If response isn't JSON, provide a minimal payload
      payload = { error: true, message: "Invalid JSON response" };
    }

    payload.status = res.status;

    if (!res.ok) {
      payload.error = true;
      payload.message = payload?.message || payload?.error || payload?.meta?.message || `HTTP ${res.status}`;
    }
    return payload;
  } catch (err: any) {
    if (err?.name === "AbortError") {
      return { error: true, message: "Request aborted" };
    }
    return { error: true, message: String(err?.message ?? err) };
  } finally {
    // Clear the timeout to prevent the AbortController from firing after the request succeeds
    clearTimeout(t);
    // Remove the event listener to prevent memory leaks
    signal.removeEventListener("abort", onAbort);
  }
}

// --- SAFE CHART API HELPERS ---

export function safeAddLineSeries(chart: IChartApi, options: any) {
  try {
    const c: any = chart as any;
    if (typeof c.addLineSeries === "function") return c.addLineSeries(options);
    if (typeof c.addSeries === "function") {
      if (LineSeries) return c.addSeries(LineSeries, options);
      return c.addSeries("Line", options);
    }
    return null;
  } catch (e) {
    console.warn("Failed to add line series", e, { options });
    return null;
  }
}

export function safeAddHistogramSeries(chart: IChartApi, options: any) {
  try {
    const c: any = chart as any;
    const safeColor = (v: unknown, fallback = "#26a69a") =>
      typeof v === "string" && v.trim().length ? v.trim() : fallback;
    const color = safeColor(options?.color);
    const safeOptions = { ...(options ?? {}), color };

    const series =
      typeof c.addHistogramSeries === "function"
        ? c.addHistogramSeries(safeOptions)
        : typeof c.addSeries === "function"
          ? HistogramSeries
            ? c.addSeries(HistogramSeries, safeOptions)
            : c.addSeries("Histogram", safeOptions)
          : null;

    // Defensive: lightweight-charts' histogram colorer can throw if it ever sees a null/empty color.
    // Enforce a safe color at creation time AND prevent later calls from wiping it out.
    if (series) {
      try {
        series.applyOptions?.({ color });
      } catch {}

      try {
        const origApplyOptions = (series as any).applyOptions;
        if (typeof origApplyOptions === "function") {
          (series as any).applyOptions = (next: any) => {
            const nextColor = safeColor(next?.color, color);
            return origApplyOptions.call(series, { ...(next ?? {}), color: nextColor });
          };
        }
      } catch {}

      try {
        const origSetData = (series as any).setData;
        if (typeof origSetData === "function") {
          (series as any).setData = (data: any) => {
            if (Array.isArray(data)) {
              const safeData = data.map((d: any) => {
                if (!d || typeof d !== "object") return d;
                const dc = (d as any).color;
                if (typeof dc === "string" && dc.length > 0) return d;
                // If caller passes null/undefined/empty color, omit it so the series-level color is used.
                // (Histogram bar colorer treats explicit null as fatal in some builds.)
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { color: _omit, ...rest } = d as any;
                return rest;
              });
              return origSetData.call(series, safeData);
            }
            return origSetData.call(series, data);
          };
        }
      } catch {}
    }

    return series;
  } catch (e) {
    console.warn("Failed to add histogram series", e, { options });
    try {
      const c: any = chart as any;
      const safeColor = (v: unknown, fallback = "#26a69a") =>
        typeof v === "string" && v.trim().length ? v.trim() : fallback;
      const minimalColor = safeColor(options?.color);
      const minimal = { color: minimalColor, baseValue: options?.baseValue ?? 0 };
      if (typeof c.addHistogramSeries === "function") return c.addHistogramSeries(minimal);
      if (typeof c.addSeries === "function") {
        if (HistogramSeries) return c.addSeries(HistogramSeries, minimal);
        return c.addSeries("Histogram", minimal);
      }
    } catch (e2) {
      console.warn("Fallback histogram series creation also failed", e2);
    }
    return null;
  }
}

export function safeAddCandlestickSeries(chart: IChartApi, options: any) {
  try {
    const c: any = chart as any;
    if (typeof c.addCandlestickSeries === "function") return c.addCandlestickSeries(options);
    if (typeof c.addSeries === "function") {
      if (CandlestickSeries) return c.addSeries(CandlestickSeries, options);
      return c.addSeries("Candlestick", options);
    }
    return null;
  } catch (e) {
    console.warn("Failed to add candlestick series", e, { options });
    try {
      const c: any = chart as any;
      const minimal = {};
      if (typeof c.addCandlestickSeries === "function") return c.addCandlestickSeries(minimal);
      if (typeof c.addSeries === "function") {
        if (CandlestickSeries) return c.addSeries(CandlestickSeries, minimal);
        return c.addSeries("Candlestick", minimal);
      }
    } catch (e2) {
      console.warn("Fallback candlestick series creation also failed", e2);
    }
    return null;
  }
}

export function safeRemoveSeries(chart: IChartApi, series: ISeriesApi<SeriesType>) {
  if (!chart || !series) return;
  try {
    chart.removeSeries(series);
  } catch {}
}
